import React, { useState, useEffect } from "react";
import {
  Modal,
  Typography,
  Stack,
  CircularProgress,
  Box,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useAppDispatch } from "../../stores/store";
import { addAgents, getSingleAgent, updateAgents } from "../../stores/agents";
import { setToast } from "../../stores/toast/toastslice";
import {
  ButtonWrapper,
  Header,
  Content,
  ModalWrapper,
  CloseButtonWrapper,
  CloseButton,
  Title,
} from "../../components/style/modalStyleLarge";
import CustomButton from "../../components/common-components/CustomButton";
import FormInputField from "../../components/common-components/FormInputField";
import { agentSchema } from "../../form-schema";
import { useFormik } from "formik";
import { CrossIcon } from "../../assets/Icon/ModalIcon";
import { addClient, updateClient } from "../../stores/clients";
import ListLoadingNew from "../../components/common-components/ListLoadingNew";

interface ModalProps {
  open: boolean;
  handleClose: () => void;
  agentId?: number | null;
  onSuccess?: () => void;
}

const AddClientModal: React.FC<ModalProps> = ({
  open,
  handleClose,
  agentId,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [getloading, setGetLoading] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const formik = useFormik({
    initialValues: {
      first_name: "",
      last_name: "",
      email: "",
    },
    validationSchema: agentSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        // Trim all string values in the payload
        const payload = Object.fromEntries(
          Object.entries(values).map(([key, value]) =>
            typeof value === "string" ? [key, value.trim()] : [key, value],
          ),
        );
        let response;

        if (agentId) {
          response = await dispatch<any>(
            updateClient({ id: agentId, ...payload }),
          ).unwrap();
        } else {
          response = await dispatch<any>(addClient(payload)).unwrap();
        
        }

        if (response?.status === 1) {
          const successMsg = response?.message;

          dispatch<any>(setToast({ type: "success", message: successMsg }));
          handleClose();
          if (onSuccess) onSuccess();
        } else {
          dispatch<any>(
            setToast({
              type: "error",
              message: response.message || "Failed to save agent.",
            }),
          );
        }
      } catch (error: any) {
  

  dispatch<any>(
    setToast({
      type: "error",
      message: error?.data?.message || "Something went wrong!",
    }),
  );
} finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    const fetchAgent = async () => {
      if (agentId) {
        setGetLoading(true);
        try {
          const response = await dispatch<any>(
            getSingleAgent(agentId),
          ).unwrap();

          if (response && response.user) {
            formik.setValues({
              first_name: response.user.first_name || "",
              last_name: response.user.last_name || "",
              email: response.user.email || "",
            });
          }
        } catch (error) {
          dispatch<any>(
            setToast({
              type: "error",
              message: "Failed to fetch agent details!",
            }),
          );
        } finally {
          setGetLoading(false);
        }
      } else {
        formik.resetForm();
      }
    };

    if (open) {
      fetchAgent();
    }
  }, [open, agentId, dispatch]);

  const handleModalClose = () => {
    formik.resetForm();
    setLoading(false);
    handleClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="delete-modal-title"
      sx={{ zIndex: 99 }}
    >
      <ModalWrapper
        sx={{
          width: isMobile ? "90%" : "45%",
          maxWidth: "550px",
          minHeight: "400px",
          maxHeight: "602px",
          margin: "auto",
          overflow: "hidden",
          p: isMobile ? 2 : 3,
        }}
      >
        <CloseButtonWrapper>
          <CloseButton onClick={handleClose}>
            <CrossIcon />
          </CloseButton>
        </CloseButtonWrapper>

        <Header
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Title
            align="center"
            sx={{ fontWeight: "bold", color: "#333", padding: "0px" }}
          >
            {agentId ? "Edit Client" : "Add Client"}
          </Title>
        </Header>

        <Content>
          {getloading ? (
            // <Box display="flex" justifyContent="center" alignItems="center">
            //   <CircularProgress />
            // </Box>
            <ListLoadingNew />
          ) : (
            <form onSubmit={formik.handleSubmit}>
              <Stack spacing={0.8}>
                {" "}
                {/* Reduced spacing */}
                <FormInputField
                  placeholder="First name"
                  formik={formik}
                  name="first_name"
                  label="First Name"
                  labelFontSize={`17px`}
                  showBorder
                />
                <FormInputField
                  placeholder="Last name"
                  formik={formik}
                  name="last_name"
                  label="Last Name"
                  labelFontSize={`17px`}
                  showBorder
                />
                <FormInputField
                  placeholder="Email address"
                  formik={formik}
                  name="email"
                  showBorder
                  label="Email Address"
                  labelFontSize={`17px`}
                  disabled={!!agentId}
                />
              </Stack>

              <ButtonWrapper
                sx={{
                  display: "flex",
                  marginTop: "15px",
                  marginBottom: "15px",
                  justifyContent: isMobile ? "center" : "space-between",
                  flexDirection: isMobile ? "column" : "row",
                  gap: isMobile ? "10px" : "2px",
                }}
              >
                <CustomButton
                  btnTitle="Cancel"
                  handleClick={handleModalClose}
                  disabled={loading}
                  minwidth={isMobile ? "100%" : "45%"}
                  bgColor="#FFF"
                  textColor="#2977CC"
                  borderColor={loading ? "transparent" : "#2977CC"}
                  borderWidth={2.3}
                  sx={{ fontSize: "18px" }}
                />

                <CustomButton
                  btnTitle={agentId ? "Update" : "Save"}
                  loading={loading}
                  type="submit"
                  disabled={loading}
                  minwidth={isMobile ? "100%" : "45%"}
                  borderColor={loading ? "transparent" : "#2977CC"}
                  sx={{ fontSize: "18px" }}
                />
              </ButtonWrapper>
            </form>
          )}
        </Content>
      </ModalWrapper>
    </Modal>
  );
};

export default AddClientModal;
